/**
 * <s-photo-wall> — Builds a grid of every photo in a source gallery and opens a tapped
 * photo in a full-screen viewer with previous / next controls. The source gallery stays
 * the single place photos are managed.
 *
 * Attributes:
 *   data-source                — Selector for the gallery whose images feed the wall.
 *   data-view-label            — Accessible label template for each tile; "__N__" becomes its number.
 * Child markup:
 *   [data-wall-grid]           — Receives one button tile per photo.
 *   dialog[data-wall-dialog]   — Viewer containing img[data-wall-image], [data-wall-count],
 *                                [data-wall-prev] and [data-wall-next].
 * Keyboard: ArrowLeft / ArrowRight step through photos while the viewer is open.
 */
class SPhotoWall extends HTMLElement {
  #abort = null;
  #photos = [];
  #index = 0;

  connectedCallback() {
    const source = document.querySelector(this.dataset.source);
    const grid = this.querySelector('[data-wall-grid]');
    const dialog = this.querySelector('[data-wall-dialog]');
    if (!source || !grid || !dialog) return;
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.#photos = Array.from(source.querySelectorAll('img')).map((img) => ({
      src: img.getAttribute('src') || '',
      srcset: img.getAttribute('srcset') || '',
      alt: img.alt || '',
    })).filter((photo) => photo.src);

    const template = this.dataset.viewLabel || '__N__';
    grid.replaceChildren(...this.#photos.map((photo, i) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.dataset.wallOpen = String(i);
      button.setAttribute('aria-haspopup', 'dialog');
      button.setAttribute('aria-label', template.replace('__N__', String(i + 1)));
      button.className = 'photo-wall-tile';
      const img = document.createElement('img');
      img.src = photo.src;
      if (photo.srcset) img.srcset = photo.srcset;
      img.sizes = '(min-width: 1024px) 16vw, (min-width: 768px) 25vw, 50vw';
      img.alt = '';
      img.loading = 'lazy';
      img.decoding = 'async';
      button.append(img);
      return button;
    }));

    this.addEventListener('click', (event) => {
      const tile = event.target.closest('[data-wall-open]');
      if (tile) {
        this.#show(Number(tile.dataset.wallOpen));
        dialog.showModal();
        return;
      }
      if (event.target.closest('[data-wall-prev]')) this.#show(this.#index - 1);
      if (event.target.closest('[data-wall-next]')) this.#show(this.#index + 1);
    }, { signal });

    dialog.addEventListener('keydown', (event) => {
      const rtl = getComputedStyle(dialog).direction === 'rtl';
      if (event.key === 'ArrowRight') this.#show(this.#index + (rtl ? -1 : 1));
      if (event.key === 'ArrowLeft') this.#show(this.#index + (rtl ? 1 : -1));
    }, { signal });
  }

  disconnectedCallback() {
    this.#abort?.abort();
  }

  #show(target) {
    const total = this.#photos.length;
    if (!total) return;
    this.#index = (target + total) % total;
    const photo = this.#photos[this.#index];
    const image = this.querySelector('[data-wall-image]');
    if (photo.srcset) image.setAttribute('srcset', photo.srcset);
    else image.removeAttribute('srcset');
    image.src = photo.src;
    image.alt = photo.alt;
    const count = this.querySelector('[data-wall-count]');
    if (count) count.textContent = `${this.#index + 1} / ${total}`;
  }
}

if (!customElements.get('s-photo-wall')) {
  customElements.define('s-photo-wall', SPhotoWall);
}
